import React, { useEffect, useState } from 'react';
import { Menu, X, Sun, Moon, Eye } from 'lucide-react';
import { motion, AnimatePresence, useSpring, useTransform, animate, useMotionValue } from 'framer-motion';
import { useTheme } from '../hooks/useTheme';
const ViewerCounter = () => {
  const count = useMotionValue(0);
  const rounded = useTransform(count, Math.round);
  useEffect(() => {
    // Random target between 1200 and 1500
    const target = Math.floor(Math.random() * (1500 - 1200 + 1) + 1200);
    const animation = animate(count, target, {
      duration: 2.5,
      ease: 'easeOut'
    });
    return animation.stop;
  }, []);
  return <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700" data-id="element-113">
      <Eye size={14} className="text-indigo-500 dark:text-indigo-400" data-id="element-114" />
      <motion.span data-id="element-115">{rounded}</motion.span>
      <span className="hidden sm:inline text-gray-400 dark:text-gray-500 font-normal" data-id="element-116">
        views
      </span>
    </div>;
};
export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {
    theme,
    toggleTheme
  } = useTheme();
  const navLinks = [{
    href: '#home',
    label: 'Home'
  }, {
    href: '#about',
    label: 'About'
  }, {
    href: '#skills',
    label: 'Skills'
  }, {
    href: '#projects',
    label: 'Projects'
  }, {
    href: '#contact',
    label: 'Contact'
  }];
  return <header className="fixed top-0 left-0 right-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md shadow-sm z-50 transition-colors border-b border-gray-100 dark:border-gray-800" data-id="element-117">
      <nav className="container mx-auto px-4 md:px-6 py-4" data-id="element-118">
        <div className="flex items-center justify-between" data-id="element-119">
          <a href="#home" className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight" data-id="element-120">
            OTO
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6" data-id="element-121">
            <div className="flex items-center space-x-6 mr-4" data-id="element-122">
              {navLinks.map(link => <a key={link.href} href={link.href} className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors" data-id="element-123">
                  {link.label}
                </a>)}
            </div>

            <div className="flex items-center gap-4 pl-4 border-l border-gray-200 dark:border-gray-700" data-id="element-124">
              <ViewerCounter data-id="element-125" />

              {/* Theme Toggle Button */}
              <motion.button onClick={toggleTheme} whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/50" aria-label="Toggle theme" data-id="element-126">
                <AnimatePresence mode="wait" data-id="element-127">
                  {theme === 'light' ? <motion.div key="moon" initial={{
                  rotate: -90,
                  opacity: 0
                }} animate={{
                  rotate: 0,
                  opacity: 1
                }} exit={{
                  rotate: 90,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-128">
                      <Moon size={18} data-id="element-129" />
                    </motion.div> : <motion.div key="sun" initial={{
                  rotate: 90,
                  opacity: 0
                }} animate={{
                  rotate: 0,
                  opacity: 1
                }} exit={{
                  rotate: -90,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-130">
                      <Sun size={18} data-id="element-131" />
                    </motion.div>}
                </AnimatePresence>
              </motion.button>
            </div>
          </div>

          {/* Mobile Menu Button & Theme Toggle */}
          <div className="md:hidden flex items-center gap-3" data-id="element-132">
            <div className="scale-90 origin-right" data-id="element-133">
              <ViewerCounter data-id="element-134" />
            </div>

            <motion.button onClick={toggleTheme} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200" aria-label="Toggle theme" data-id="element-135">
              <AnimatePresence mode="wait" data-id="element-136">
                {theme === 'light' ? <motion.div key="moon" initial={{
                rotate: -90,
                opacity: 0
              }} animate={{
                rotate: 0,
                opacity: 1
              }} exit={{
                rotate: 90,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-137">
                    <Moon size={20} data-id="element-138" />
                  </motion.div> : <motion.div key="sun" initial={{
                rotate: 90,
                opacity: 0
              }} animate={{
                rotate: 0,
                opacity: 1
              }} exit={{
                rotate: -90,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-139">
                    <Sun size={20} data-id="element-140" />
                  </motion.div>}
              </AnimatePresence>
            </motion.button>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-1 text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400" aria-label="Toggle menu" data-id="element-141">
              {isMenuOpen ? <X size={24} data-id="element-142" /> : <Menu size={24} data-id="element-143" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence data-id="element-144">
          {isMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="md:hidden mt-4 border-t border-gray-100 dark:border-gray-800" data-id="element-145">
              <div className="py-4 space-y-1" data-id="element-146">
                {navLinks.map((link, index) => <motion.a key={link.href} href={link.href} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.05,
              duration: 0.3
            }} onClick={() => setIsMenuOpen(false)} className="block px-4 py-3 rounded-lg text-base font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all" data-id="element-147">
                    {link.label}
                  </motion.a>)}
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>
    </header>;
};